
import React, { useState } from "react";
import axios from "axios";

const { getNewYorkDateTime } = require("../utils/dateUtils");

const PRODUCTS = ["43_MD"];

const CRM_URL =
  "https://crm-server-htk6.vercel.app/api/agents";

const ORDER_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000/api/test-order-43md"
    : "https://cbsapi.i-crm.cc/api/test-order-43md";

export default function Api43md() {
  const [, setAgents] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingRow, setLoadingRow] = useState({});

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Product dropdown
  const [product, setProduct] = useState("");

  // Update row in both states
  const patchRow = (id, fields) => {
    const apply = (prev) =>
      prev.map((a) =>
        a._id === id ? { ...a, ...fields } : a
      );

    setAgents(apply);
    setFiltered(apply);
  };

  // Safe text
  const safeText = (value) => {
    if (value === null || value === undefined) {
      return "";
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return value;
  };

  // Process individual 43_MD record
  const processSingleAgent = async (row) => {
    // Only 43_MD is supported
    if (product !== "43_MD") {
      alert("Please select 43_MD first.");
      return;
    }

    // Confirm duplicate successful charge
    if (row.MD43APIStatus === "Success") {
      const confirmed = window.confirm(
        "This record was already charged successfully. Charge again?"
      );

      if (!confirmed) return;
    }

    setLoadingRow((prev) => ({
      ...prev,
      [row._id]: true,
    }));

    let updateFields;

    // STEP 1: Charge API
    try {
      const [expMonth = "", expYear = ""] =
        row.expiry?.split("/") || [];

      const payload = {
        APIKey: "37e3f554-d6d4-4a26-b08b-63c0d1ee11e3",
        domain: "wv4.com",
        buildorder: 1,
        MerchantID: "0",
        capture_delay: 0,
        amount: "43.92",
        bincheck: 1,
        skipfulfillment: 1,
        ProductCount: 1,
        productid_1: "466",

        card_num:
          row.cardNumber?.replace(/[-\s]/g, "") || "",

        card_expm: expMonth.padStart(2, "0"),

        card_expy:
          expYear.length === 2
            ? `20${expYear}`
            : expYear,

        card_cvv: row.cvv,

        CustomerFirstName: row.firstName,
        CustomerLastName: row.lastName,

        BillingStreetAddress: row.address,
        BillingApt: row.apt || "",
        BillingCity: row.city,
        BillingState: row.state,
        BillingZipCode: row.zip,

        Email: row.email,

        BillingHomePhone: (row.phone || "").replace(
          /\D/g,
          ""
        ),

        DOB: row.dob,
      };

      const res = await axios.post(ORDER_URL, payload);

      let data = res.data?.data;

      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Invalid API response JSON:", e);
          data = {};
        }
      }

      const isSuccess =
        Number(data?.ResponseCode) === 200;

      updateFields = {
        MD43APIStatus: isSuccess ? "Success" : "Failed",

        MD43APIResponseCode:
          data?.ResponseCode || "",

        MD43APIResponseMessage:
          typeof data?.ResponseData === "object"
            ? JSON.stringify(data.ResponseData)
            : data?.ResponseData || "",

        MD43APIDate: getNewYorkDateTime(),

        MD43ApiOrderNumber:
          data?.OrderNumber || "",
      };

    } catch (error) {
      console.error(
        "43_MD Row API Error:",
        error.response?.data || error.message
      );

      const apiErr =
        error.response?.data?.apiError;

      const errorMessage = apiErr
        ? typeof apiErr === "string"
          ? apiErr
          : JSON.stringify(apiErr)
        : error.response?.data?.error ||
          error.message;

      // Network errors may have an uncertain payment outcome.
      patchRow(row._id, {
        MD43APIStatus: "Unknown",
        MD43APIResponseMessage: errorMessage,
        MD43APIDate: getNewYorkDateTime(),
      });

      alert(
        "The API request did not return a confirmed result. Verify the transaction before retrying."
      );

      setLoadingRow((prev) => ({
        ...prev,
        [row._id]: false,
      }));

      return;
    }

    // STEP 2: Save result to CRM
    try {
      await axios.put(
        `${CRM_URL}/${row._id}`,
        updateFields
      );

    } catch (e) {
      console.error(
        "Charge completed but CRM save failed:",
        row._id,
        updateFields,
        e.response?.data || e.message
      );

      // Update current table even if CRM save fails
      patchRow(row._id, updateFields);

      alert(
        `Charge result: ${updateFields.MD43APIStatus} (Order ${
          updateFields.MD43ApiOrderNumber || "N/A"
        }). CRM save failed. Do NOT charge again until the CRM record is verified.`
      );

      setLoadingRow((prev) => ({
        ...prev,
        [row._id]: false,
      }));

      return;
    }

    // STEP 3: Update UI
    patchRow(row._id, updateFields);

    setLoadingRow((prev) => ({
      ...prev,
      [row._id]: false,
    }));
  };

  // Filter records by product and date
  const handleFilter = async () => {
    if (!product) {
      alert("Please select a product first.");
      return;
    }

    if (fromDate && toDate && fromDate > toDate) {
      alert("From Date cannot be later than To Date.");
      return;
    }

    try {
      setLoading(true);

      const params = new URLSearchParams();

      params.append("product", product);

      if (fromDate && toDate) {
        params.append("fromDate", fromDate);
        params.append("toDate", toDate);
      }

      const res = await axios.get(
        `${CRM_URL}/product-filter?${params.toString()}`
      );

      setAgents(res.data);
      setFiltered(res.data);

    } catch (err) {
      console.error(
        "Error fetching filtered agents:",
        err.response?.data || err.message
      );

      alert("Error fetching records. Please try again.");

    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ padding: "20px" }}
      className="allforms-container"
    >
      <h2>Charge Processing – {product || "Select Product"}</h2>

      {loading && (
        <div className="loading-popup">
          <p>Processing... Please wait</p>
        </div>
      )}

      {/* FILTER SECTION */}
      <div
        className="form-row"
        style={{
          display: "flex",
          gap: "10px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {/* PRODUCT DROPDOWN */}
        <select
          value={product}
          onChange={(e) => setProduct(e.target.value)}
          style={{
            padding: "8px 12px",
            fontSize: "14px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            minWidth: "150px",
          }}
        >
          <option value="">Select Product</option>

          {PRODUCTS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>

        {/* FROM DATE */}
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={{
            padding: "8px",
            borderRadius: "5px",
            border: "1px solid #ccc",
          }}
        />

        {/* TO DATE */}
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={{
            padding: "8px",
            borderRadius: "5px",
            border: "1px solid #ccc",
          }}
        />

        {/* GO BUTTON */}
        <button
          className="btn save"
          onClick={handleFilter}
          disabled={loading}
          style={{
            padding: "8px 16px",
            fontSize: "14px",
            borderRadius: "5px",
            backgroundColor: "#4CAF50",
            color: "#fff",
            border: "none",
            cursor: "pointer",
          }}
        >
          Go
        </button>
      </div>

      {/* TABLE SECTION */}
      {loading ? (
        <p>⏳ Loading...</p>
      ) : (
        <div
          className="table-container1"
          style={{
            marginTop: "20px",
            overflowX: "auto",
          }}
        >
          <table
            className="report-table table-container"
            style={{
              borderCollapse: "collapse",
              width: "100%",
            }}
          >
            <thead>
              <tr>
                <th>CustomerID</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Charge</th>
                <th>Status</th>
                <th>ApiOrderNumber</th>
                <th>Message</th>
                <th>APIDate</th>
              </tr>
            </thead>

            <tbody>
              {!product ? (
                <tr>
                  <td
                    colSpan="10"
                    style={{ textAlign: "center" }}
                  >
                    Please select a product and click Go.
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan="10"
                    style={{ textAlign: "center" }}
                  >
                    No records found
                  </td>
                </tr>
              ) : (
                filtered.map((a) => (
                  <tr
                    key={a._id}
                    style={{
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    <td>{a.CustomerID || ""}</td>

                    <td>{a.firstName || ""}</td>

                    <td>{a.lastName || ""}</td>

                    <td>{a.email || ""}</td>

                    <td>{a.phone || ""}</td>

                    {/* CHARGE BUTTON */}
                    <td>
                      <button
                        className="btn save"
                        onClick={() =>
                          processSingleAgent(a)
                        }
                        disabled={loadingRow[a._id]}
                        style={{
                          padding: "6px 12px",
                          fontSize: "13px",
                          backgroundColor: "#007bff",
                          color: "#fff",
                          borderRadius: "5px",
                          cursor: "pointer",
                          border: "none",
                        }}
                      >
                        {loadingRow[a._id] ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Processing...
                          </>
                        ) : (
                          "Post API"
                        )}
                      </button>
                    </td>

                    {/* 43_MD STATUS */}
                    <td>
                      {safeText(a.MD43APIStatus)}
                    </td>

                    {/* ORDER NUMBER */}
                    <td>
                      {safeText(a.MD43ApiOrderNumber)}
                    </td>

                    {/* RESPONSE MESSAGE */}
                    <td>
                      {safeText(
                        a.MD43APIResponseMessage
                      )}
                    </td>

                    {/* API DATE */}
                    <td>
                      {safeText(a.MD43APIDate)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
